<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AgentAssignmentList from './AgentAssignmentList.vue';

const props = defineProps({
  modelValue: {
    type: [Object, String, null],
    default: null,
  },
  options: {
    type: Array,
    required: true,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  dropdownMaxHeight: {
    type: String,
    default: 'max-h-80',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const open = ref(false);

const selectedKey = computed(() => {
  if (!props.modelValue) return null;
  return typeof props.modelValue === 'object'
    ? props.modelValue.id
    : props.modelValue;
});

const selectedOption = computed(
  () =>
    props.options.find(option => option.id === selectedKey.value) || {
      id: 'nil',
      name: 'Unassigned',
    }
);

const selectedLabel = computed(
  () => selectedOption.value?.name || props.placeholder || ''
);

const triggerStateClass = computed(() => {
  if (props.hasError) {
    return '!border-n-ruby-8 !outline-n-ruby-8 dark:!border-n-ruby-8 dark:!outline-n-ruby-8';
  }
  if (props.disabled) {
    return 'cursor-not-allowed opacity-60 border-n-weak outline-n-weak dark:border-n-strong dark:outline-n-strong';
  }
  if (open.value) {
    return '!border-n-blue-8 !outline-n-blue-8';
  }
  return 'border-n-weak outline-n-weak hover:border-n-slate-7 hover:outline-n-slate-7 dark:border-n-strong dark:outline-n-strong dark:hover:border-n-slate-7 dark:hover:outline-n-slate-7';
});

const buildListItem = option => {
  const isAiAgent = String(option.id).startsWith('ai_agent:');
  const isUnassigned = option.id === 'nil';

  if (isUnassigned) {
    return {
      label: 'Unassigned',
      value: option.id,
      action: 'clearSelection',
      icon: 'i-lucide-user-round',
      iconClass: 'text-n-ruby-9',
      iconContainerClass: 'bg-n-ruby-4/80',
      isSelected: option.id === selectedKey.value,
    };
  }

  if (isAiAgent) {
    return {
      label: option.name,
      value: option.id,
      emoji: option.emoji || '🤖',
      isSelected: option.id === selectedKey.value,
    };
  }

  return {
    label: option.available_name || option.name,
    value: option.id,
    thumbnail: {
      name: option.available_name || option.name,
      src: option.thumbnail || option.avatar_url || '',
    },
    availabilityStatus: option.availability_status,
    isSelected: option.id === selectedKey.value,
  };
};

const menuSections = computed(() => {
  const aiAgents = [
    {
      label: t('AI_AGENTS.ACTIONS.NEW'),
      value: '__create_ai_agent__',
      action: 'createAiAgent',
      emoji: '🤖',
      isSelected: false,
      itemClass: 'bg-n-blue-4/40 text-n-blue-11 hover:!bg-n-blue-4/60',
    },
  ];
  const people = [];

  props.options.forEach(option => {
    const item = buildListItem(option);

    if (String(option.id).startsWith('ai_agent:')) {
      aiAgents.push(item);
      return;
    }

    people.push(item);
  });

  return [
    ...(aiAgents.length
      ? [
          {
            title: t('CHAT_LIST.OMNI_SIDEBAR.AI_AGENTS'),
            badgeLabel: t('GENERAL.BETA'),
            items: aiAgents,
          },
        ]
      : []),
    {
      items: people,
    },
  ].filter(section => section.items.length > 0);
});

const close = () => {
  open.value = false;
};

const toggle = () => {
  if (props.disabled) return;
  open.value = !open.value;
};

const openCreateAiAgent = () => {
  router.push({
    name: 'ai_agents_new',
    params: { accountId: route.params.accountId },
  });
  close();
};

const selectOption = item => {
  if (item.action === 'createAiAgent') {
    openCreateAiAgent();
    return;
  }

  if (item.action === 'clearSelection') {
    emit('update:modelValue', null);
    emit('change', { id: 'nil', name: 'Unassigned' });
    close();
    return;
  }

  const option = props.options.find(entry => entry.id === item.value);
  if (!option) return;

  emit('update:modelValue', {
    id: option.id,
    name: option.name,
  });
  emit('change', option);
  close();
};
</script>

<template>
  <div v-on-click-outside="close" class="relative w-full">
    <button
      type="button"
      class="flex h-[32px] w-full items-center gap-2 rounded-lg border border-solid bg-transparent px-3 py-1 text-start outline outline-1 -outline-offset-1 transition-colors"
      :class="triggerStateClass"
      :aria-label="ariaLabel || t('COMBOBOX.PLACEHOLDER')"
      :aria-expanded="open"
      :disabled="disabled"
      @click="toggle"
    >
      <span
        v-if="selectedOption?.id?.startsWith?.('ai_agent:')"
        class="inline-flex size-[18px] shrink-0 items-center justify-center text-sm"
      >
        {{ selectedOption.emoji || '🤖' }}
      </span>
      <span
        v-else-if="selectedOption?.id === 'nil'"
        class="inline-flex size-[18px] shrink-0 items-center justify-center rounded-md bg-n-ruby-4/80"
      >
        <span class="i-lucide-user-round text-xs text-n-ruby-9" />
      </span>
      <Avatar
        v-else-if="selectedOption"
        :name="selectedOption.available_name || selectedOption.name"
        :src="selectedOption.thumbnail || selectedOption.avatar_url || ''"
        :status="selectedOption.availability_status"
        :size="18"
      />
      <span
        class="min-w-0 flex-1 truncate h-[18px] text-[15px] font-normal leading-[18px] text-n-slate-12"
      >
        {{ selectedLabel }}
      </span>
      <span
        class="inline-flex h-5 w-5 min-h-5 min-w-5 shrink-0 items-center justify-center text-[24px] leading-none text-n-slate-11"
        :class="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        aria-hidden="true"
      />
    </button>

    <div
      v-if="open"
      class="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-n-strong bg-n-slate-3 shadow-lg"
      role="listbox"
    >
      <AgentAssignmentList
        :sections="menuSections"
        :thumbnail-size="20"
        search-placeholder="Search"
        :max-height-class="dropdownMaxHeight"
        @select="selectOption"
      />
    </div>
  </div>
</template>
